import { computed, effect, inject, Signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';

import { catchError, map, tap, throwError } from 'rxjs';

import { signalStore, withComputed, withHooks, withMethods, withProps } from '@ngrx/signals';
import { Store } from '@ngrx/store';

import { V1_AuthService } from '@app/api/v1/auth/v1-auth.service';
import { SlnkAbilityService } from '@app/auth/ability/ability.service';
import { mapAbilityConditionProperties, packApiAbilityRules } from '@app/auth/ability/ability.utils';
import { selectUserUuid } from '@app/auth/store/selectors/user.selectors';
import { AbilityAction, AbilityConditionProperty, AbilitySubject } from '@app/models/api/v1/auth/ability.model';

export const AbilityStore = signalStore(
  { providedIn: 'root' },
  withProps(() => {
    const authService = inject(V1_AuthService);
    const ngrxStore = inject(Store);
    const ability = inject(SlnkAbilityService);
    const userUuid = toSignal(ngrxStore.select(selectUserUuid), { initialValue: null });

    const abilityResource = rxResource({
      params: () => userUuid() ?? undefined,
      stream: ({ params: _userUuid }) =>
        authService.getAbility().pipe(
          map(response => packApiAbilityRules(response.data)),
          tap(rules => ability.update(rules)),
          catchError(error => {
            ability.update([]);
            return throwError(() => error);
          }),
        ),
    });

    return { userUuid, abilityResource };
  }),
  withComputed(store => ({
    entityConditionProperties: computed(() => mapAbilityConditionProperties(store.abilityResource.value() ?? [])),
  })),
  withMethods(store => ({
    reload(): void {
      store.abilityResource.reload();
    },
    hasEntityConditionProperty(
      action: AbilityAction,
      subject: AbilitySubject,
      entityConditionProperty: AbilityConditionProperty,
    ): Signal<boolean> {
      return computed(() => store.entityConditionProperties()[subject]?.[action]?.includes(entityConditionProperty) ?? false);
    },
    isGlobalScope(action: AbilityAction, subject: AbilitySubject): Signal<boolean> {
      return computed(() => store.entityConditionProperties()[subject]?.[action]?.length === 0);
    },
  })),
  withHooks({
    onInit(store) {
      const ability = inject(SlnkAbilityService);

      effect(() => {
        if (!store.userUuid()) {
          ability.update([]);
        }
      });
    },
  }),
);
